/**
 * @file instance.ts
 * @description The app-wide i18next instance and the one sanctioned way to
 * change the active UI locale.
 *
 * Separate from `index.ts` so the barrel can re-export the provider without a
 * dependency cycle: `I18nProvider` needs the instance, and a barrel that both
 * exported the provider and owned the instance would import itself.
 * @module i18n
 */
import { createInstance } from "i18next";
import { initReactI18next } from "react-i18next";

import { isUiLocalizationEnabled } from "./killSwitch";
import { buildInitOptions } from "./options";

import { DEFAULT_UI_LOCALE, resolveDisplayLocale } from "@/constants/locales";

import type { i18n as I18nInstance } from "i18next";

/**
 * Creates and initializes an i18next instance.
 *
 * Exported so tests can build an isolated instance instead of mutating the
 * shared singleton — a test that reconfigures the singleton leaks its state
 * into every later test in the same worker.
 *
 * `init` returns a promise, but it is already settled when this returns: all
 * six catalogs are bundled statically and `initImmediate: false` is set, so
 * there is no async backend to await. The promise is explicitly discarded
 * rather than awaited so the module-scope singleton below stays synchronous.
 * @param options - Configuration for the instance
 * @param options.enabled - Kill-switch state; false pins the instance to English
 * @param options.lng - Initial locale; ignored when the kill-switch is off
 * @returns An initialized i18next instance
 */
export function createI18nInstance({
  enabled = isUiLocalizationEnabled(),
  lng = DEFAULT_UI_LOCALE,
}: {
  readonly enabled?: boolean;
  readonly lng?: string;
} = {}): I18nInstance {
  const instance = createInstance();
  void instance.use(initReactI18next).init(buildInitOptions({ enabled, lng }));
  return instance;
}

/** The app-wide i18next instance. */
export const i18n: I18nInstance = createI18nInstance();

/**
 * Sets the active UI locale, resolving a stored `languagePreference` through
 * {@link resolveDisplayLocale} first so a stranded locale (`ja_JP`) degrades to
 * an offered one rather than to a raw code.
 *
 * When the kill-switch is off this is a no-op and the runtime stays English.
 * That is a second lock on top of `supportedLngs: ["en_US"]`, so a caller
 * reaching for `changeLanguage` directly still cannot escape the switch.
 * @param preference - A stored languagePreference, a bare subtag, or nothing
 * @param instance - The instance to change; defaults to the app-wide one
 * @returns A promise resolving once the language change has settled
 */
export async function setAppLocale(
  preference: string | null | undefined,
  instance: I18nInstance = i18n
): Promise<void> {
  if (!isUiLocalizationEnabled()) return;
  await instance.changeLanguage(resolveDisplayLocale(preference));
}
